import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import InputAdornment from '@mui/material/InputAdornment'

import {
  useCurrentMove,
  useGetLocationLabel,
  useSelectedQuote,
  getLocationFieldName,
  getLocationType,
  type UpdateMovePayload,
} from '../../../../modules/moves'
import { getUsedLocations } from '../../../../modules/moves/core/locations.utils'
import { LocationType } from '../../../../modules/moves/core/moves.enums'
import { mergeClassName } from '../../../../utils/mergeClassName'
import { Row, Cell } from '../../../data'
import InfoTooltip from '../../../InfoTooltip'
import Form from '../../../Form'
import Input, { InputSize } from '../../../Form/controls/Input'
import SubmitBar from '../SubmitBar'
import useValidationSchema from './validationSchema'
import { type AddressesFormValues } from './AddressesForm.type'
import { formatFormOutput, moveToFormData } from './formDataBuilder'
import LocationFields from './LocationFields'

type AddressesFormProps = {
  onSubmit: (data: UpdateMovePayload) => Promise<void> | void
  onCancel?: () => void
  wide?: boolean
}

const AddressesForm: React.FC<AddressesFormProps> = ({
  onCancel,
  onSubmit,
  wide = false,
}) => {
  const { t } = useTranslation()
  const move = useCurrentMove()
  const selectedQuote = useSelectedQuote()

  const specificAddresses = !!selectedQuote

  const schema = useValidationSchema({ move, specificAddresses })
  const form = useForm({
    resolver: yupResolver(schema),
    defaultValues: moveToFormData(move, { specificAddresses }),
  })

  const handleSubmit = async (values: AddressesFormValues) => {
    await onSubmit(formatFormOutput(values))
  }

  const usedLocations = getUsedLocations(move)
  const getLocationLabel = useGetLocationLabel()

  return (
    <Form
      form={form}
      onSubmit={handleSubmit}
    >
      <Row className={mergeClassName(
        'gap-x-4',
        !wide && 'lg:flex-col',
      )}
      >

        { usedLocations.map(usedLocation => {
          const type = getLocationType(move.locations, usedLocation.id)
          const isStop = type === LocationType.Stop
          const name = `locations.${getLocationFieldName(move.locations, usedLocation.id)}`
          const label = getLocationLabel(move.locations, usedLocation.id)

          return (
            <Cell key={usedLocation.id}>
              <h3 className="my-2 font-bold text-black">
                { label }
              </h3>

              <LocationFields
                name={name}
                specificAddresses={specificAddresses}
                wide={wide}
              >
                { isStop && (
                  <div className="mb-2 flex flex-col items-center gap-2 md:flex-row">
                    <div className="grow">
                      <Input
                        name={`${name}.description`}
                        placeholder={t('forms.fields.stopDescription.label')}
                        size={InputSize.Small}
                      />
                    </div>

                    <div className="flex items-center gap-x-2">
                      <div className="w-[150px] shrink-0">
                        <Input
                          name={`${name}.stopDurationHours`}
                          size={InputSize.Small}
                          maxLength={4}
                          endNode={(
                            <InputAdornment position="end">
                              { t('units.hour_s') }
                            </InputAdornment>
                          )}
                        />
                      </div>
                      <InfoTooltip
                        tooltip={t('components.move.addresses.durationTooltip')}
                      />
                    </div>
                  </div>
                ) }
              </LocationFields>
            </Cell>
          )
        }) }

        { /* { ['origin', 'destination'].map(address => {
          const isOrigin = address === 'origin'
          const isDestination = address === 'destination'

          return (
            <Cell key={address}>
              <h3 className="my-2 font-bold text-black">
                { isOrigin && t('components.move.addresses.movingFrom') }
                { isDestination && t('components.move.addresses.movingTo') }
              </h3>

              <LocationFields
                name={address}
                specificAddresses={specificAddresses}
                wide={wide}
              />

              { isOrigin && (
                <Stops
                  specificAddresses={specificAddresses}
                />
              ) }

            </Cell>
          )
        }) } */ }

      </Row>

      <SubmitBar onCancel={onCancel} />
    </Form>
  )
}

export default AddressesForm
